import type { StoredSupportCitation } from './supportChatStorage';

// Source-only references: (/guides/foo), *(/guides/foo, /guides/bar)*,
// or ([Title](/guides/foo)). The replacement still requires matching API citations.
const PARENTHETICAL_SOURCE_REFERENCES =
  /(?<!\])\*?\(((?:\/[^()\n]+|\[[^\]\n]+\]\([^()\n]+\))(?:,\s*(?:\/[^()\n]+|\[[^\]\n]+\]\([^()\n]+\)))*)\)\*?/g;

export function safeSupportLink(url: string) {
  // The API can return root-relative documentation URLs when no guide host is configured.
  if (/^\/(?![/\\])/.test(url) && !/[\\\s]/.test(url)) return true;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

export function supportAnswerSources(text: string, citations: StoredSupportCitation[] = []) {
  const validSources = citations.filter(
    (citation, index) =>
      safeSupportLink(citation.url) &&
      citations.findIndex((other) => other.url === citation.url) === index
  );
  const paths = new Set(
    validSources.flatMap((source) => {
      const url = new URL(source.url, 'https://documentation.invalid');
      return [source.urlPath, `${source.urlPath}${url.hash}`];
    })
  );
  // Only remove source-only parentheticals that match returned citations. Preserve prose,
  // unknown references, and fenced/inline code rather than guessing a destination.
  const answer = text
    .split(/(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/g)
    .map((part, index) => {
      if (index % 2) return part;
      return part
        .replace(PARENTHETICAL_SOURCE_REFERENCES, (original, references: string) => {
          const refs = references.split(',').map((reference) => {
            const value = reference.trim();
            const markdownLink = /^\[[^\]]+\]\(([^)]+)\)$/.exec(value);
            return markdownLink ? markdownLink[1] : value;
          });
          return refs.every((reference) => paths.has(reference)) ? '' : original;
        })
        .replace(/\n[ \t]*\n(?:[ \t]*\n)+/g, '\n\n');
    })
    .join('')
    .trim();
  // One row per guide: prefer a section explicitly referenced in the answer.
  const sourcesByGuide = new Map<string, StoredSupportCitation>();
  for (const source of validSources) {
    const url = new URL(source.url, 'https://documentation.invalid');
    const key = `${url.origin}${url.pathname}`;
    const existing = sourcesByGuide.get(key);
    const reference = `${source.urlPath}${url.hash}`;
    if (
      !existing ||
      (text.includes(reference) &&
        !text.includes(
          `${existing.urlPath}${new URL(existing.url, 'https://documentation.invalid').hash}`
        ))
    ) {
      sourcesByGuide.set(key, source);
    }
  }
  return { answer, sources: [...sourcesByGuide.values()] };
}
